import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useSettings, applyTheme } from "@/context/SettingsContext";
import { Check, UploadSimple, Trash, DownloadSimple, DatabaseIcon } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PRESETS = [
  { name: "Графіт (типовий)", color: "#334155" },
  { name: "Океан", color: "#006ba9" },
  { name: "Індиго", color: "#4f46e5" },
  { name: "Смарагд", color: "#059669" },
  { name: "Бірюза", color: "#0d9488" },
  { name: "Багряний", color: "#dc2626" },
  { name: "Фіолет", color: "#7c3aed" },
  { name: "Бурштин", color: "#b45309" },
];

export default function Settings() {
  const { settings, reload } = useSettings();
  const [color, setColor] = useState("#334155");
  const [logoPath, setLogoPath] = useState(undefined); // undefined = unchanged
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const exportBackup = async () => {
    setExporting(true);
    try {
      const r = await api.get("/backup/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-T:]/g, "").replace(/(\d{8})(\d{4})/, "$1-$2");
      a.download = `sto-backup-${ts}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Резервну копію завантажено");
    } catch (e) { toast.error("Не вдалося створити резервну копію"); }
    finally { setExporting(false); }
  };

  const importBackup = async (fileList) => {
    const file = (fileList || [])[0];
    if (!file) return;
    if (!window.confirm("Відновити з резервної копії? Поточні дані (клієнти, наряди, фінанси, користувачі, медіа) буде ЗАМІНЕНО вмістом архіву. Цю дію не можна скасувати.")) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/backup/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Відновлено з копії (${r.data.media_files} медіафайлів). Оновіть сторінку.`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  useEffect(() => {
    setColor(settings.primary_color || "#006ba9");
    setLogoPreview(settings.logo_url ? `${BACKEND}${settings.logo_url}` : null);
  }, [settings]);

  const pick = (c) => { setColor(c); applyTheme(c); };

  const onUpload = async (fileList) => {
    const file = (fileList || [])[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoPath(r.data.path);
      setLogoPreview(URL.createObjectURL(file));
      toast.success("Лого завантажено — не забудьте зберегти");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const removeLogo = () => { setLogoPath(""); setLogoPreview(null); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { primary_color: color };
      if (logoPath !== undefined) payload.logo_path = logoPath;
      await api.put("/settings", payload);
      await reload();
      setLogoPath(undefined);
      toast.success("Налаштування збережено");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Налаштування" subtitle="Зовнішній вигляд та брендинг системи" />

      <Card className="p-6 mb-6" data-testid="settings-color-card">
        <h3 className="font-head font-bold text-lg mb-1">Кольорова схема</h3>
        <p className="text-sm text-muted-foreground mb-4">Основний колір застосовується до меню, шапки та кнопок.</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PRESETS.map((p) => (
            <button
              key={p.color}
              onClick={() => pick(p.color)}
              data-testid={`preset-${p.color}`}
              className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${color.toLowerCase() === p.color.toLowerCase() ? "border-slate-900 ring-2 ring-offset-1 ring-slate-300" : "border-border hover:border-slate-400"}`}
            >
              <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: p.color }}>
                {color.toLowerCase() === p.color.toLowerCase() && <Check size={14} weight="bold" className="text-white" />}
              </span>
              <span className="text-xs text-left leading-tight">{p.name}</span>
            </button>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-3">
          <Label htmlFor="custom-color" className="text-sm">Свій колір:</Label>
          <input
            id="custom-color"
            type="color"
            value={color}
            onChange={(e) => pick(e.target.value)}
            data-testid="custom-color-input"
            className="w-12 h-9 rounded border border-border cursor-pointer bg-transparent"
          />
          <span className="font-mono text-sm uppercase text-muted-foreground">{color}</span>
        </div>
      </Card>

      <Card className="p-6 mb-6" data-testid="settings-logo-card">
        <h3 className="font-head font-bold text-lg mb-1">Логотип</h3>
        <p className="text-sm text-muted-foreground mb-4">Відображається у меню та на сторінці входу (на кольоровому фоні — світлим силуетом).</p>

        <div className="flex items-center gap-5 flex-wrap">
          <div className="h-20 w-56 rounded-lg flex items-center justify-center p-3" style={{ background: "var(--brand)" }}>
            {logoPreview
              ? <img src={logoPreview} alt="Лого" className="max-h-full max-w-full brightness-0 invert" data-testid="logo-preview" />
              : <img src="/viwat-logo.svg" alt="VIWAT" className="max-h-full max-w-full brightness-0 invert opacity-80" />}
          </div>
          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white border border-slate-300 hover:bg-slate-50 text-sm cursor-pointer transition-colors" data-testid="logo-upload-label">
              <UploadSimple size={18} /> {uploading ? "Завантаження..." : "Завантажити лого"}
              <input type="file" accept="image/*" hidden disabled={uploading}
                onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }}
                data-testid="logo-upload-input" />
            </label>
            {logoPreview && (
              <button onClick={removeLogo} data-testid="logo-remove-btn"
                className="inline-flex items-center gap-2 text-sm text-destructive hover:underline">
                <Trash size={16} /> Прибрати лого
              </button>
            )}
            <span className="text-xs text-muted-foreground">PNG / SVG, до 12 МБ. Прозорий фон рекомендовано.</span>
          </div>
        </div>
      </Card>

      <Card className="p-6 mb-6" data-testid="settings-backup-card">
        <h3 className="font-head font-bold text-lg mb-1 flex items-center gap-2"><DatabaseIcon size={20} /> Резервне копіювання</h3>
        <p className="text-sm text-muted-foreground mb-4">Експорт створює архів (.zip) з усією базою даних та медіафайлами (фото авто, діагностика, лого). Імпорт відновлює систему з такого архіву.</p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={exportBackup} disabled={exporting} data-testid="export-backup-btn">
            <DownloadSimple size={18} className="mr-1.5" /> {exporting ? "Формування..." : "Експорт бекапа"}
          </Button>
          <label className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm cursor-pointer transition-colors ${importing ? "opacity-60 pointer-events-none" : ""}`} data-testid="import-backup-label">
            <UploadSimple size={18} /> {importing ? "Відновлення..." : "Імпорт бекапа"}
            <input type="file" accept=".zip,application/zip" hidden disabled={importing}
              onChange={(e) => { importBackup(e.target.files); e.target.value = ""; }}
              data-testid="import-backup-input" />
          </label>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Зберігайте архіви у надійному місці. Імпорт повністю замінює поточні дані.</p>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
          {saving ? "Збереження..." : "Зберегти налаштування"}
        </Button>
      </div>
    </div>
  );
}
